import { styled } from '@linaria/react';
import { Group, Textarea } from '@mantine/core';
import type { ChangeEvent, KeyboardEvent } from 'react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import type { SlashCommand } from '../../../../api/agents-client/agent-client.type';
import type { MentionItem, SelectedMention } from '../../../flora/components/flora-mention/flora-mention.type';
import { MentionPill } from '../../../flora/components/flora-mention/mention-pill';
import { MentionPopover } from '../../../flora/components/flora-mention/mention-popover';
import { useMention } from '../../../flora/components/flora-mention/use-mention';
import { SlashCommandPopover } from '../../../flora/components/chat/slash-commands/slash-command-popover';
import { useSlashCommandInput } from '../../../flora/components/chat/slash-commands/use-slash-command-input';
import { inputStyles } from './input-styles';

interface FloraPromptTextareaHandle {
  /** Inserts a token at the textarea's current caret position (or appends it when the textarea is not focused). */
  insertAtCursor: (token: string) => void;
  /** Focuses the underlying textarea. */
  focus: () => void;
  /**
   * Closes the mention/slash popovers. Call this when the editor is hidden while
   * kept mounted (e.g. an action-type toggle), since the popovers render in a
   * Portal that the anchor's `display:none` cannot hide.
   */
  closePopovers: () => void;
}

interface FloraPromptTextareaProps {
  value: string;
  onChange: (value: string) => void;
  /**
   * Called whenever the picked mentions change. The parent must keep these and
   * pass them to `formatPromptWithMentions(value, mentions)` before persisting,
   * which substitutes the in-editor `[@Name]` placeholders for their wire format.
   */
  onMentionsChange?: (mentions: SelectedMention[]) => void;
  /** When true, typing `/` also opens the slash-command popover. Defaults to false. */
  enableSlashCommands?: boolean;
  placeholder?: string;
  minRows?: number;
  maxRows?: number;
  maxLength?: number;
  readOnly?: boolean;
  id?: string;
}

/**
 * Textarea with Flora "@ mention" tagging (and optional "/ slash command") support.
 *
 * Encapsulates the `useMention` / `useSlashCommandInput` hooks, the popovers, the
 * selected-mention pills, and the keyboard/caret wiring so every prompt editor
 * (slash commands, automations) shares one implementation.
 *
 * Mentions live in the editor as `[@Name]` placeholder text plus a selections
 * array surfaced via `onMentionsChange`; callers serialize them with
 * `formatPromptWithMentions` on save.
 *
 * @param {FloraPromptTextareaProps} props - The controlled value, change handlers, and textarea options
 * @param {React.Ref<FloraPromptTextareaHandle>} ref - Imperative handle exposing `insertAtCursor` / `focus`
 * @returns {JSX.Element} The pills, popovers, and textarea
 */
export const FloraPromptTextarea = forwardRef<FloraPromptTextareaHandle, FloraPromptTextareaProps>(
  (
    {
      value,
      onChange,
      onMentionsChange,
      enableSlashCommands = false,
      placeholder,
      minRows = 6,
      maxRows = 14,
      maxLength,
      readOnly,
      id,
    },
    ref,
  ) => {
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const wrapperRef = useRef<HTMLDivElement | null>(null);

    const {
      isOpen: isMentionOpen,
      searchQuery: mentionSearchQuery,
      allItems: mentionAllItems,
      boards: mentionBoards,
      selectedMentions,
      handleInputChange: handleMentionInputChange,
      handleSelectMention,
      handleRemoveMention,
      closeMentionPopover,
    } = useMention(value);

    const {
      isOpen: isSlashOpen,
      searchQuery: slashSearchQuery,
      filteredGroups: slashFilteredGroups,
      flatFilteredCommands: slashFlatCommands,
      handleInputChange: handleSlashInputChange,
      handleSelectCommand: handleSlashSelectCommand,
      closePopover: closeSlashPopover,
    } = useSlashCommandInput(value);

    useEffect(() => {
      onMentionsChange?.(selectedMentions);
    }, [selectedMentions, onMentionsChange]);

    const focusCaret = useCallback((position: number): void => {
      requestAnimationFrame(() => {
        const element = textareaRef.current;
        if (!element) return;
        element.focus();
        element.setSelectionRange(position, position);
      });
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        insertAtCursor: (token: string): void => {
          const element = textareaRef.current;
          const start = element?.selectionStart ?? value.length;
          const end = element?.selectionEnd ?? value.length;
          const nextValue = `${value.slice(0, start)}${token}${value.slice(end)}`;
          onChange(nextValue);
          focusCaret(start + token.length);
        },
        focus: (): void => {
          textareaRef.current?.focus();
        },
        closePopovers: (): void => {
          closeMentionPopover();
          closeSlashPopover();
        },
      }),
      [value, onChange, focusCaret, closeMentionPopover, closeSlashPopover],
    );

    const handleChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
      const nextValue = event.currentTarget.value;
      const cursor = event.currentTarget.selectionStart || 0;
      onChange(nextValue);
      if (enableSlashCommands) handleSlashInputChange(nextValue, cursor);
      handleMentionInputChange(nextValue, cursor);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
      const isNavKey =
        event.key === 'Enter' || event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Tab';
      if (enableSlashCommands && isSlashOpen) {
        if (isNavKey) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          closeSlashPopover();
          return;
        }
      }
      if (isMentionOpen) {
        if (isNavKey) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          closeMentionPopover();
        }
      }
    };

    const handleMentionSelect = (item: MentionItem): void => {
      const { newValue, newCursorPosition } = handleSelectMention(item);
      onChange(newValue);
      focusCaret(newCursorPosition);
    };

    const handleSlashSelect = (command: SlashCommand): void => {
      const { newValue, newCursorPosition } = handleSlashSelectCommand(command);
      onChange(newValue);
      focusCaret(newCursorPosition);
    };

    const handleMentionPillRemove = (mentionId: string, mentionName: string): void => {
      const placeholder = `[@${mentionName}]`;
      const stripped = value.replace(placeholder, '').replace(/ {2,}/g, ' ').trim();
      onChange(stripped);
      handleRemoveMention(mentionId);
    };

    return (
      <>
        {selectedMentions.length > 0 && (
          <MentionPillsContainer>
            <Group gap={6}>
              {selectedMentions.map((mention) => (
                <MentionPill
                  key={`${mention.type}-${mention.id}-${mention.startIndex}`}
                  mention={mention}
                  readonly={readOnly}
                  onRemove={readOnly ? undefined : () => handleMentionPillRemove(mention.id, mention.name)}
                />
              ))}
            </Group>
          </MentionPillsContainer>
        )}
        <PromptInputBox ref={wrapperRef}>
          <MentionPopover
            isOpen={isMentionOpen && !isSlashOpen}
            items={mentionAllItems}
            boards={mentionBoards}
            searchQuery={mentionSearchQuery}
            onSelect={handleMentionSelect}
            onClose={closeMentionPopover}
            anchorRef={wrapperRef}
          />
          {enableSlashCommands && (
            <SlashCommandPopover
              isOpen={isSlashOpen && !isMentionOpen}
              groups={slashFilteredGroups}
              flatCommands={slashFlatCommands}
              searchQuery={slashSearchQuery}
              onSelect={handleSlashSelect}
              onClose={closeSlashPopover}
              anchorRef={wrapperRef}
            />
          )}
          <Textarea
            id={id}
            placeholder={placeholder}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            minRows={minRows}
            autosize
            maxRows={maxRows}
            maxLength={maxLength}
            readOnly={readOnly}
            ref={textareaRef}
            styles={inputStyles}
          />
        </PromptInputBox>
      </>
    );
  },
);

FloraPromptTextarea.displayName = 'FloraPromptTextarea';

const PromptInputBox = styled.div`
  position: relative;
`;

const MentionPillsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

export type { FloraPromptTextareaHandle, FloraPromptTextareaProps };
